// httpPorts — the Packaging BC slice: the export/package flow. `exportMaterial`
// mints the download URL, `loadExport` reads the package view, `downloadExport`
// fetches the bytes (impure DOM I/O).

import { Result } from "@bilda/utils";
import type {
    ExportContents,
    ExportFormatKind,
    FormatLoss,
    PackagedFrom,
    PackageFile,
} from "../../components/ExportView/ExportView.js";
import type {
    MaterialRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import { mkMalformed } from "../externalError.js";
import { API_BASE } from "../httpClient.js";
import type { PackagingPorts } from "../ports/packaging.js";
import type { PortResult } from "../ports/result.js";
import type { ExportReady, LoadedExport, MaterialId } from "../ports.js";
import { fields, list, num, str } from "../wireShape.js";
import { command } from "./shared.js";

// `showResource` with the package kind-guard. A package resource EXISTING
// means its build completed (the `CreateExportResource` PM only runs on
// `packaging_completed`), so the status is always `ready` and `/exports/:id`
// can render it — there is no persisted building/failed state to read.

const loadExport = async (exportId: string): PortResult<LoadedExport> => {
    const r = await command("showResource", {
        id: exportId,
        expectedKind: "export",
    });
    if (Result.isErr(r)) return r;
    const loaded = loadedExportOf(exportId, fields(r.value)?.get("view"));
    return loaded === undefined
        ? Result.err(mkMalformed("showResource: no resource in view"))
        : Result.ok(loaded);
};

// The package view, read off the wire (pure). `view.package` carries what the
// package was made from, what its format left out, and the delivered file(s);
// an absent block (an older server) reads as "unrecorded", nothing, none.
const loadedExportOf = (
    exportId: string,
    rawView: unknown,
): LoadedExport | undefined => {
    const view = fields(rawView);
    const resource = fields(view?.get("resource"));
    if (resource === undefined) return undefined;
    const title = str(resource.get("title"));
    const packagingId = str(
        fields(
            fields(resource.get("packagingProvenance"))?.get("packagingRef"),
        )?.get("id"),
    );
    const pkg = fields(view?.get("package"));
    const contents: ExportContents = {
        format: formatKind(packagingId),
        packagedFrom: packagedFromOf(pkg?.get("packagedFrom")),
        leftOut: [
            ...list(pkg?.get("findings")).flatMap(formatLossOf),
            ...list(pkg?.get("resolution")).flatMap(resolutionLossOf),
        ],
        files: list(pkg?.get("files")).flatMap(packageFileOf),
    };
    return {
        contents,
        status: { type: "ready", downloadName: title, findings: [] },
        downloadUrl: `${API_BASE}/exports/${exportId}`,
    };
};

const refOf = (raw: unknown): { id: string; title: string } | undefined => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    const title = str(f?.get("title"));
    return id === undefined || title === undefined ? undefined : { id, title };
};

const packagedFromOf = (raw: unknown): PackagedFrom => {
    const f = fields(raw);
    const version = num(f?.get("version"));
    switch (str(f?.get("type"))) {
        case "packaged_from_material": {
            const ref = refOf(f?.get("material"));
            if (ref === undefined || version === undefined) break;
            const material: MaterialRef = { type: "material", ...ref };
            const sources = list(f?.get("sources")).flatMap(
                (s): SourceRef[] => {
                    const src = refOf(s);
                    return src !== undefined &&
                        str(fields(s)?.get("kind")) === "source"
                        ? [{ type: "source", ...src }]
                        : [];
                },
            );
            return { type: "material", material, version, sources };
        }
        case "packaged_from_removed_material": {
            const title = str(f?.get("title"));
            if (title === undefined || version === undefined) break;
            return { type: "removed-material", title, version };
        }
    }
    return { type: "unrecorded" };
};

// Core's `CategoricalLoss` tags, typed; the view owns the words.
const formatLossOf = (raw: unknown): FormatLoss[] => {
    const tag = str(raw);
    switch (tag) {
        case undefined:
            return [];
        case "lost_interactivity":
            return [{ type: "interactivity" }];
        case "lost_styling":
            return [{ type: "styling" }];
        case "lost_grading_logic":
            return [{ type: "grading" }];
        case "lost_audio_metadata":
            return [{ type: "audio-details" }];
        case "lost_objective_alignment":
            return [{ type: "objective-alignment" }];
        default:
            return [{ type: "other", id: tag }];
    }
};

// Export's resolution report (core `ResolutionFinding`), typed: what it
// could not include + the embedded sources to check. An inlined part is
// carried, so it is not a loss; an unknown finding is skipped.
const resolutionLossOf = (raw: unknown): FormatLoss[] => {
    const f = fields(raw);
    const title = str(f?.get("title")) ?? str(f?.get("resourceId"));
    if (title === undefined) return [];
    switch (str(f?.get("type"))) {
        case "reference_target_removed":
            return [{ type: "part-removed", title }];
        case "reference_content_unavailable":
            return [{ type: "part-unavailable", title }];
        case "reference_part_planned":
            return [{ type: "part-planned", title }];
        case "reference_part_unreviewed":
            return [{ type: "part-unreviewed", title }];
        case "reference_kind_unsupported":
            return [{ type: "part-not-includable", title }];
        case "part_not_in_module":
            return [{ type: "part-not-in-module", title }];
        case "container_flattened":
            return [{ type: "container-flattened", title }];
        case "reference_cycle":
            return [{ type: "part-loop", title }];
        case "embedded_source_not_attached":
            return [{ type: "source-not-attached", title }];
        case "embedded_source_rights_check":
            return [{ type: "source-rights", title }];
        default:
            return [];
    }
};

const packageFileOf = (raw: unknown): PackageFile[] => {
    const name = str(fields(raw)?.get("name"));
    return name === undefined ? [] : [{ name }];
};

// The package resource carries no format id directly — the Packaging that
// produced it does, spelled into its id's tail (`P_pqbl_torus_torus` targets
// `F_torus`, `P_pqbl_torus_canvas` targets `F_canvas`, `P_quiz_markdown`
// targets `F_markdown`). Typed from that tail; the view owns the words. An
// unrecognised id travels raw as `other`.
export const formatKind = (
    packagingId: string | undefined,
): ExportFormatKind => {
    if (packagingId === undefined) return { type: "unspecified" };
    if (packagingId.endsWith("_torus")) return { type: "torus" };
    if (packagingId.endsWith("_canvas")) return { type: "canvas" };
    if (packagingId.endsWith("_markdown")) return { type: "markdown" };
    if (packagingId.endsWith("_html")) return { type: "html" };
    if (packagingId.endsWith("_text")) return { type: "text" };
    return { type: "other", id: packagingId };
};

// Package the material for a target format through the SAME primitive the agent
// uses: `POST /commands/packageMaterial` lands a `package` Resource and returns
// its `outputResourceId`. The download URL is the `/exports/:id` route that
// re-renders that package's bytes (the same link the agent hands the author). A
// refusal (unsupported target for the template, material not authored) arrives
// as an `ok:false` envelope → typed `ExternalError` the host shows as `failed`.

const exportMaterial = async (
    materialId: MaterialId,
    targetFormat: string,
): PortResult<ExportReady> => {
    const r = await command("packageMaterial", {
        materialId,
        targetFormat,
    });
    if (Result.isErr(r)) return r;
    const outputResourceId = str(fields(r.value)?.get("outputResourceId"));
    if (outputResourceId === undefined) {
        return Result.err(
            mkMalformed("packageMaterial returned no outputResourceId"),
        );
    }
    return Result.ok({
        downloadUrl: `${API_BASE}/exports/${outputResourceId}`,
    });
};

// Trigger the browser's attachment download. The `/exports/:id` response carries
// `Content-Disposition: attachment`, so a synthetic anchor click saves the file
// WITHOUT navigating away (the server's manifest-derived filename wins). Impure
// DOM I/O, isolated to this infra port; the host only reports the intent up.

const downloadExport = (url: string): void => {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export { loadedExportOf };

export const packagingHttp: PackagingPorts = {
    exportMaterial,
    downloadExport,
    loadExport,
};
